// ---------------------------------------------------------------------------
// Public profile, at /u/:username.
//
// Privacy is the constraint that shapes this file. The only personal facts a
// profile may ever show are the username, the display name, and the games that
// account has played. No email address, no Discord handle, no IP, no device id,
// and no field the API does not already return. `GET /api/u/:username` is built
// to the same rule, so the honest way to keep this screen safe is to render
// exactly `ProfileResponse` and add nothing to it.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ProfileResponse } from '@countdown/shared';
import { getProfile } from '../lib/api.js';
import { gameRecordPath, ordinal } from '../lib/format.js';
import { Card, CardTitle } from '../components/primitives/index.js';
import { BestOf } from './profile/BestOf.js';
import { Distributions } from './profile/Distributions.js';

type Load =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; profile: ProfileResponse };

/**
 * `api.ts` throws an Error carrying the server's sentence rather than a status
 * code, so a missing player is recognised by what the server said. A miss here
 * only downgrades the kind empty state to the plain failure state.
 */
function isMissing(error: Error): boolean {
  return /no such player/i.test(error.message) || /\(404\)/.test(error.message);
}

function shortDate(at: number): string {
  return new Date(at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function monthAndYear(at: number): string {
  return new Date(at).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
}

export function ProfileScreen() {
  const { username } = useParams<{ username: string }>();
  /** Tagged with the username it answers, so a stale reply cannot be shown. */
  const [answer, setAnswer] = useState<{ username: string; load: Load } | null>(null);

  useEffect(() => {
    if (!username) return;

    let live = true;

    getProfile(username)
      .then((profile) => {
        if (live) setAnswer({ username, load: { status: 'ready', profile } });
      })
      .catch((error: Error) => {
        if (!live) return;
        setAnswer({
          username,
          load: isMissing(error)
            ? { status: 'missing' }
            : { status: 'failed', message: error.message },
        });
      });

    return () => {
      live = false;
    };
  }, [username]);

  // Derived rather than pushed through state by the effect: a link with no
  // username is answerable without asking the server, and a reply belonging to
  // the previous username is not this profile's answer, so navigating between
  // two profiles reads as loading instead of flashing the one before it.
  const load: Load = !username
    ? { status: 'missing' }
    : answer?.username === username
      ? answer.load
      : { status: 'loading' };

  return (
    <main className="mx-auto w-full max-w-[720px] px-4 py-6 sm:px-6">
      {load.status === 'loading' && (
        <p className="py-16 text-center text-ink-dim" aria-live="polite">
          Loading this profile.
        </p>
      )}

      {load.status === 'missing' && <MissingPlayer username={username ?? ''} />}

      {load.status === 'failed' && (
        <div className="py-16 text-center">
          <h1 className="text-2xl font-bold">That profile did not load</h1>
          <p className="mt-3 text-ink-muted">{load.message}</p>
          <Link to="/" className="mt-6 inline-block min-h-11 leading-11 text-blue-200 underline">
            Back to the home page
          </Link>
        </div>
      )}

      {load.status === 'ready' && <Profile profile={load.profile} />}
    </main>
  );
}

/**
 * A username that was never taken is an ordinary outcome, not an error. It gets a
 * sentence and a way onward, not a status code.
 */
function MissingPlayer({ username }: { username: string }) {
  return (
    <div className="py-16 text-center">
      <h1 className="text-2xl font-bold">No player by that name</h1>
      <p className="mx-auto mt-3 max-w-[380px] text-ink-muted text-pretty">
        {username
          ? `Nobody here goes by ${username}. Usernames are permanent, so this one is either spelled differently or still free.`
          : 'This link is missing a username.'}
      </p>
      <Link to="/browse" className="mt-6 inline-block min-h-11 leading-11 text-blue-200 underline">
        Find a room to join
      </Link>
    </div>
  );
}

function Profile({ profile }: { profile: ProfileResponse }) {
  const { stats, recentGames } = profile;

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-3xl font-bold">{profile.displayName}</h1>
          {/* The boxed mono slot, per the identity rule: a display name may itself
              contain parentheses, so a handle rendered inside the name string
              would be indistinguishable from part of the name. */}
          <span className="rounded-chip border border-line bg-surface px-2 py-1 font-mono text-xs text-ink-muted">
            @{profile.username}
          </span>
        </div>
        <p className="text-sm text-ink-dim">Playing since {monthAndYear(profile.createdAt)}</p>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="games played" value={stats.gamesPlayed} />
        <Stat label="wins" value={stats.wins} />
        <Stat label="total score" value={stats.totalScore} />
        <Stat label="conundrums solved" value={stats.conundrumsSolved} />
      </div>

      <BestOf stats={stats} />

      <Distributions distributions={profile.distributions} />

      <Card>
        <CardTitle>Recent games</CardTitle>
        {recentGames.length === 0 ? (
          <p className="mt-3 text-sm text-ink-dim">
            No finished games yet. Games count once they reach the end.
          </p>
        ) : (
          <ul className="mt-3 flex flex-col divide-y divide-line">
            {recentGames.map((game) => (
              <li key={game.gameId}>
                {/* The whole row is the link. The game id is in the href and
                    nowhere in the text: it is not personal, but it is not
                    something a reader has any use for either. */}
                <Link
                  to={gameRecordPath(game.gameId)}
                  className="flex items-center justify-between gap-3 py-3 hover:bg-surface"
                >
                  <div>
                    <p className="flex items-center gap-2 font-medium">
                      {ordinal(game.placement)} of {game.playerCount}
                      {/* **Listed, and counted nowhere.** A game played with
                          host rulings on is written and has a page, and it is in
                          this list because it happened and this player was in
                          it — a history that quietly omitted an afternoon would
                          be its own kind of dishonesty. None of the totals above
                          include it, so without this mark the arithmetic on this
                          page does not add up and there is nothing saying why. */}
                      {game.ranked === 0 ? (
                        <span
                          data-testid="unranked-game"
                          title="Host rulings were on, so this game counts towards no statistics."
                          className="rounded-[5px] bg-raised px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-[0.06em] text-ink-dim uppercase"
                        >
                          unranked
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-ink-dim">{shortDate(game.at)}</p>
                  </div>
                  <p className="tabular font-mono text-lg font-semibold">{game.score}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-card border border-line bg-surface px-3 py-3">
      <p className="label-mono">{label}</p>
      <p className="tabular mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
}
